import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, Renderer2, OnDestroy } from '@angular/core';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { ListsService } from '../../services/lists.service';
import { CommonModule } from '@angular/common';
import { signal } from '@angular/core';

@Component({
  selector: 'app-fonpen',
  imports: [
    CommonModule,
    ReactiveFormsModule
  ],
  templateUrl: './fonpen.component.html',
  styleUrls: ['./fonpen.component.scss']
})
export class FonpenComponent implements OnInit, AfterViewInit, OnDestroy {

  elements: any[] = [];
  filtered: any[] = [];
  form: FormGroup;
  
  // Signals para control de timeout
  offTimeOut = signal(false);
  hasWritten = signal(false);
  hasSelected = signal(false);
  private countdownTimer: any;
  private viewInitialized = signal(false);

  @Input() defaultValue: any = null;
  @Output() valueState = new EventEmitter<number | null>();
  @ViewChild('element') element!: ElementRef;
  @ViewChild('subtitleLabel') subtitleLabel!: ElementRef;
  @ViewChild('inbox') inbox!: ElementRef;
  
  isValid = false;

  constructor(
    private _listService: ListsService,
    private renderer: Renderer2
  ) {
    this.form = new FormGroup({
      element: new FormControl('', [Validators.required]),
    });

    this.form.valueChanges.subscribe((value) => {
      this.hasWritten.set(true);
      
      if (this.form.valid && value.element && this.hasSelected()) {
        this.clearCountdown();
        this.offTimeOut.set(false);
        this.getStyleEnabled();
      }
    });
  }
   
  ngOnInit(): void {
    this._listService.getPensioner().subscribe({
      next: (response: any) => {
        this.elements = response;
      }
    });
  }

  ngAfterViewInit(): void {
    this.viewInitialized.set(true);
    this.applyInitialStyles();

    if (this.defaultValue) {
      this.setDefaultValue(this.defaultValue);
    } else {
      this.startCountdown();
    }
  }

  private applyInitialStyles() {
    this.getStyleEnabled();
  }

  private safeApplyStyles(callback: () => void) {
    if (this.viewInitialized()) {
      callback();
    }
  }

  // Métodos para el control de timeout y estilos
  startCountdown() {
    this.clearCountdown();
    
    this.countdownTimer = setTimeout(() => {

      if (!this.hasSelected() || !this.form.valid) {
        this.offTimeOut.set(true);
        this.getStyleError();
      }
    }, 8000); // 8 segundos
  }

  clearCountdown() {
    if (this.countdownTimer) {
      clearTimeout(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  getStyleError(){
    this.safeApplyStyles(() => {
      if (this.subtitleLabel?.nativeElement && this.inbox?.nativeElement) {
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-enabled')
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-disabled')
        this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label-error');
        this.renderer.addClass(this.inbox.nativeElement, 'border-error')
      }
    });
  }
  
  getStyleEnabled(){
    this.safeApplyStyles(() => {
      if (this.subtitleLabel?.nativeElement && this.inbox?.nativeElement) {
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-disabled')
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-error');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-error')
        this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label');
        this.renderer.addClass(this.inbox.nativeElement, 'border-enabled')
      }
    });
  }

  getStyleTextDisabled(){
    this.safeApplyStyles(() => {
      if (this.subtitleLabel?.nativeElement && this.inbox?.nativeElement) {
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-enabled')
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-error');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-error')
        this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
        this.renderer.addClass(this.inbox.nativeElement, 'border-disabled')
      }
    });
  }

  filter(event: any) {
    this.isValid = false;
    this.hasSelected.set(false); // Al filtrar, se resetea la selección
    
    const query = event.target.value.toLowerCase();
    this.filtered = this.elements.filter(location =>
      location.label.toLowerCase().includes(query)
    );
  }

  select(value: number, label: string) {
    this.form.get('element')!.setValue(label);
    this.isValid = true;
    this.filtered = [];
    this.valueState.emit(value);
    
    // Marcar como seleccionado y limpiar timeout
    this.hasSelected.set(true);
    this.clearCountdown();
    this.offTimeOut.set(false);
    this.getStyleEnabled();
  }

  esc() {
    this.filtered = [];
    this.isValid = false;
    this.form.get('element')!.reset();
    this.valueState.emit(null);
    this.hasSelected.set(false);
    
    // Reiniciar el countdown cuando se limpia
    this.startCountdown();
  }

  makeInvalid() {
    this.isValid = false;
    this.valueState.emit(null);
    this.hasSelected.set(false);
  }

  setDefaultValue(defaultValue: any) {
    setTimeout(() => {
      this.form.get('element')!.setValue(defaultValue);
      this.isValid = true;
      this.hasSelected.set(true);
      
      const objs = this.elements.find(element => element.value == defaultValue);
      if(objs){
        this.element.nativeElement.value = objs.label;
      }
      
      // Limpiar timeout si hay valor por defecto
      this.clearCountdown();
      this.offTimeOut.set(false);
      this.getStyleEnabled();
    });
  }

  validateBlur() {
    setTimeout( () => {
      if(!this.isValid) {
        this.esc();
      }
    }, 1000);
  }

  hasError = () => {
    const shouldShowError = this.offTimeOut() && (!this.hasSelected() || !this.form.valid);
    return shouldShowError;
  }

  ngOnDestroy() {
    this.clearCountdown();
  }
}